import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { clearToken, getToken, logout, setUnauthorizedHandler } from "./auth";

/* Who is signed in, which workspace they are acting in, what their plan
   allows, and the platform's public switches - read once from /auth/me and
   shared, so the sidebar, the banner and the route guards agree. */
const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  // "checking" until the stored token is confirmed, so a revoked session
  // never flashes the workspace.
  const [status, setStatus] = useState(getToken() ? "checking" : "anonymous");
  const [me, setMe] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setStatus("anonymous");
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      setStatus("authed");
      return data;
    } catch (err) {
      // Only a 401 means the session is gone. A network error or a 5xx means
      // the backend is unwell - throwing the user out would just lose a good
      // token and land them back here after signing in again.
      if (err?.response?.status === 401) {
        clearToken();
        setMe(null);
        setStatus("anonymous");
      } else {
        setStatus("unreachable");
      }
      return null;
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setMe(null);
      setStatus("anonymous");
    });
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await logout();
    setMe(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({
      status,
      me,
      refresh,
      signOut,
      isAdmin: Boolean(me?.is_super_admin),
      // Owners and admins manage the team and billing-type settings.
      canManage: ["owner", "admin"].includes(me?.role),
    }),
    [status, me, refresh, signOut]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);

/* The public platform switches, for pages a visitor sees before signing in. */
export function usePlatformStatus() {
  const [platform, setPlatform] = useState(null);
  useEffect(() => {
    let live = true;
    api
      .get("/platform/status")
      .then(({ data }) => live && setPlatform(data))
      // Unreachable backend: assume the safe default rather than flashing a
      // half-launched site.
      .catch(() => live && setPlatform({ maintenance: true, signups_open: false }));
    return () => {
      live = false;
    };
  }, []);
  return platform;
}
